import React from 'react';
import { TableRow, TableCell, Box, Typography, Chip } from '@mui/material';
import { Description as QueryIcon } from '@mui/icons-material';
import { colors, fonts, spacing } from '../../constants';
import { formatQueryType, getLifecycleStatusMeta, normalizeLifecycleStatus, getLastUpdatedTimestamp } from '../../utils/queryListUtils';
import { formatDateTime } from '../../utils/dateUtils';
import { formatPostShortName } from '../../services/postSelection';
import QueryTableActions from './QueryTableActions';

/**
 * QueryTableRow Component
 * Individual query row in table
 */
const QueryTableRow = ({
  query,
  onView,
  onDelete,
  onReopen,
  onClose,
  reopeningId,
  closingId,
  tableBodyCellStyles,
  tableRowStyles,
}) => {
  const lastUpdatedAt = getLastUpdatedTimestamp(query);
  const normalizedLabel = normalizeLifecycleStatus(query.statusLabel);
  const normalizedStatus = normalizeLifecycleStatus(query.status);
  const lifecycleMeta =
    getLifecycleStatusMeta(normalizedLabel) ||
    getLifecycleStatusMeta(normalizedStatus);

  return (
    <TableRow
      key={query._id}
      hover
      sx={{
        ...tableRowStyles,
        backgroundColor: '#ffffff',
        transition: 'all 0.2s ease',
        borderLeft: `2px solid transparent`,
        borderTop: `1px solid ${colors.borderDefault}`,
        '& .MuiTableCell-root': {
          borderTop: `1px solid ${colors.borderDefault} !important`,
          borderBottom: `1px solid ${colors.borderDefault} !important`,
        },
        '&:hover': {
          backgroundColor: '#fafbfc',
          borderLeft: `2px solid ${colors.primaryDark}`,
          borderTop: `1px solid ${colors.borderDefault}`,
          transform: 'translateX(2px)',
          '& .MuiTableCell-root': {
            borderTop: `1px solid ${colors.borderDefault} !important`,
            borderBottom: `1px solid ${colors.borderDefault} !important`,
          },
        },
      }}
    >
      <TableCell sx={{ ...tableBodyCellStyles, pl: spacing.lg, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
          <QueryIcon sx={{ fontSize: '14px', color: colors.primaryDark, opacity: 0.7 }} />
          <Typography sx={{ fontWeight: fonts.weightMedium, fontSize: '0.8125rem', color: '#1f2937' }}>
            {formatQueryType(query.queryType)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography 
          sx={{ 
            fontWeight: fonts.weightMedium,
            color: '#1f2937',
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
          }}
        >
          {query.queryId || query._id}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography sx={{ fontWeight: fonts.weightMedium, fontSize: '0.8125rem', color: '#1f2937' }}>
          {query.assignee?.name || 'N/A'}
        </Typography>
        {query.postShortName && (
          <Typography sx={{ fontSize: '0.6875rem', color: '#6b7280' }}>
            {formatPostShortName(query.postShortName)}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        {lifecycleMeta ? (
          <Chip
            label={lifecycleMeta.label}
            size="small"
            sx={{
              backgroundColor: `${lifecycleMeta.color}1a`,
              color: lifecycleMeta.color,
              fontWeight: fonts.weightSemiBold,
              fontSize: '0.7rem',
              alignSelf: 'flex-start',
            }}
          />
        ) : null}
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <Typography 
          sx={{ 
            fontSize: '0.8125rem',
            color: '#6b7280',
          }}
        >
          {lastUpdatedAt ? formatDateTime(lastUpdatedAt) : 'N/A'}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableBodyCellStyles, textAlign: 'right', width: '280px', minWidth: '280px', py: spacing.sm, borderBottom: `1px solid ${colors.borderDefault} !important`, borderTop: `1px solid ${colors.borderDefault} !important` }}>
        <QueryTableActions
          query={query}
          onView={onView}
          onDelete={onDelete}
          onReopen={onReopen}
          onClose={onClose}
          reopeningId={reopeningId}
          closingId={closingId}
        />
      </TableCell>
    </TableRow>
  );
};

export default QueryTableRow;
